import React, { PointerEvent, useState } from 'react'
import { isEqual } from 'lodash'
import { View, StyleSheet, Platform } from 'react-native'
import PropTypes from 'prop-types'
import { dataTypes } from '@adalo/constants'
import { IObject } from 'interfaces'
import { BindingContextConsumerComponent } from 'components/ContextConsumerComponent'
import ActionWrapper from 'components/ActionWrapper'
import TextCell from './TextCell'
import NumberCell from './NumberCell'
import DateCell from './DateCell'
import LocationCell from './LocationCell'
import FileCell from './FileCell'
import ImageCell from './ImageCell'
import BooleanCell from './BooleanCell'
import { type CellData } from './TableTypes'
import { type TableTheme } from './theme'

export type Row = {
  id: number
  cells: CellData[]
}

type TableRowContentProps = {
  data: Row
  theme: TableTheme
}

type TableRowProps = TableRowContentProps & {
  tableObject: IObject
  datasource: { datasourceId: string; tableId: string }
}

const TableRowContent: React.FC<TableRowContentProps> = ({ data, theme }) => {
  const [hovered, setHovered] = useState(false)

  const renderCell = (cell: CellData) => {
    switch (cell.type) {
      case dataTypes.TEXT:
        return <TextCell cell={cell} />
      case dataTypes.NUMBER:
        return <NumberCell cell={cell} />
      case dataTypes.DATE:
      case dataTypes.DATE_ONLY:
        return <DateCell cell={cell} />
      case dataTypes.LOCATION:
        return <LocationCell cell={cell} />
      case dataTypes.FILE:
        return <FileCell cell={cell} />
      case dataTypes.IMAGE:
        return <ImageCell cell={cell} />
      case dataTypes.BOOLEAN:
        return <BooleanCell cell={cell} />
      default:
        return null
    }
  }

  const viewProps = {
    style: [styles.row, theme.row, hovered && theme.rowHover],
    // TODO(michael-adalo): https://reactnative.dev/blog/2022/12/13/pointer-events-in-react-native
    ...(Platform.OS === 'web' && {
      onPointerEnter: (e: PointerEvent<HTMLDivElement>) => {
        setHovered(true)
      },
      onPointerLeave: (e: PointerEvent<HTMLDivElement>) => {
        setHovered(false)
      },
    }),
  }

  return (
    <View {...viewProps}>
      {data.cells.map((cell: CellData) => (
        <View key={cell.fieldId} style={[styles.cell, cell.style]}>
          {renderCell(cell)}
        </View>
      ))}
    </View>
  )
}

const hasAction = (obj: IObject) =>
  Boolean(obj.actions && Object.keys(obj.actions).length > 0)

class TableRow extends BindingContextConsumerComponent<TableRowProps> {
  static childContextTypes = {
    getBinding: PropTypes.func,
    getBindings: PropTypes.func,
    getBindingsList: PropTypes.func,
  }

  getChildContext() {
    return {
      getBinding: this.getBinding,
      getBindings: this.getBindings,
      getBindingsList: this.getBindingsList,
    }
  }

  getBinding = (objectId: string) => {
    const { data, tableObject } = this.props
    const { getBinding } = this.context

    if (
      objectId === tableObject.id ||
      objectId === (tableObject.dataBinding && tableObject.dataBinding.id)
    ) {
      try {
        return { ...data, id: JSON.parse(tableObject.id) }
      } catch {
        return data
      }
    } else if (getBinding) {
      return getBinding(objectId)
    }
  }

  getBindings = () => {
    const { data, datasource } = this.props

    const { getBindings } = this.context

    let result = {}

    if (getBindings) {
      result = getBindings()
    }

    if (data) {
      const key = `${datasource.datasourceId}.${datasource.tableId}`
      result[key] = data.id
    }

    return result
  }

  getBindingsList = () => {
    const { getBindingsList } = this.context
    const { data } = this.props

    const bindingsList = getBindingsList()

    if (data) return bindingsList.concat([data.id])
    return bindingsList
  }

  render() {
    const { tableObject } = this.props
    const { component } = this.context

    const row = <TableRowContent {...this.props} />
    if (hasAction(tableObject)) {
      return (
        <ActionWrapper
          bindingData={{}}
          object={tableObject}
          component={component}
          hitSlop={0}
        >
          {row}
        </ActionWrapper>
      )
    }

    return row
  }
}

export const styles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#ccc',
    alignItems: 'center',
    minHeight: 55,
  },
  cell: {
    flex: 1,
    alignItems: 'flex-start',
    padding: 10,
    paddingLeft: 20,
  },
  cellText: {
    textAlign: 'left',
    fontSize: 13,
    fontWeight: '500',
    lineHeight: 15.25,
  },
})

export default React.memo(TableRow, isEqual)
